import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { BookOpen, CheckCircle2, Loader2, LogIn, LogOut, MonitorSmartphone, UserCheck, Wallet, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { KeypadEntry } from '@/components/staffAttendance/KeypadEntry';
import { SelfieStep } from '@/components/staffAttendance/SelfieStep';
import { kioskApi, kioskConfig, errMsg, fmtTime, kindLabel, WORK_ROLES } from '@/services/staffAttendanceApi';
import { WorkRolePicker } from '@/components/staffAttendance/WorkRole';
import { GUIDES } from '@/components/staffAttendance/GuideMenu';
import { getGPS } from '@/services/transportApi';

const RESET_MS = 5000;
const IDLE_MS = 90000;

const beep = (ok) => {
  try {
    navigator.vibrate?.(ok ? 200 : [100, 80, 100]);
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.frequency.value = ok ? 880 : 220; o.connect(g); g.connect(ctx.destination); g.gain.value = 0.15;
    o.start(); o.stop(ctx.currentTime + 0.25);
  } catch { /* no audio */ }
};

const Clock = () => {
  const [t, setT] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setT(new Date()), 15000); return () => clearInterval(i); }, []);
  return <span className="font-mono text-2xl" data-testid="kiosk-clock">{t.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}</span>;
};

const NotSetUp = ({ onSetup }) => (
  <div className="max-w-lg mx-auto text-center space-y-6 pt-16" data-testid="kiosk-not-setup">
    <MonitorSmartphone className="w-20 h-20 mx-auto text-amber-400" />
    <h1 className="text-3xl font-bold">This device is not set up</h1>
    <p className="text-lg text-slate-300">Supervisor: log in on this device → <b>Staff Attendance</b> → <b>Section Devices</b> → <b>Set up this device</b>.</p>
    <Button onClick={onSetup} className="h-14 px-8 text-lg bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="kiosk-setup-btn">Set up (supervisor login)</Button>
  </div>
);

const Done = ({ result }) => (
  <div className={`max-w-lg mx-auto text-center space-y-5 pt-10 ${result.ok ? '' : 'text-red-200'}`} data-testid="kiosk-done">
    {result.ok ? <CheckCircle2 className="w-28 h-28 mx-auto text-emerald-400" /> : <XCircle className="w-28 h-28 mx-auto text-red-400" />}
    <h2 className="text-4xl font-bold" data-testid="kiosk-done-title">{result.title}</h2>
    <p className="text-xl text-slate-300" data-testid="kiosk-done-msg">{result.msg}</p>
  </div>
);

export default function AttendanceKiosk() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(kioskConfig.get());
  const [device, setDevice] = useState(null);
  const [step, setStep] = useState('id'); // id | confirm | selfie | done
  const [sid, setSid] = useState('');
  const [staff, setStaff] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [role, setRole] = useState(null);
  const timer = useRef(null);

  const reset = useCallback(() => { clearTimeout(timer.current); setStep('id'); setSid(''); setStaff(null); setError(''); setResult(null); setBusy(false); setRole(null); }, []);

  useEffect(() => {
    if (!cfg) return;
    kioskApi.me().then(setDevice).catch((e) => { if (e?.response?.status === 401) { kioskConfig.clear(); setCfg(null); } });
  }, [cfg]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (step === 'done') timer.current = setTimeout(reset, RESET_MS);
    else if (step !== 'id') timer.current = setTimeout(reset, IDLE_MS);
    return () => clearTimeout(timer.current);
  }, [step, reset]);

  const lookup = async () => {
    setBusy(true); setError('');
    try { setStaff(await kioskApi.lookup(sid)); setStep('confirm'); } catch (e) { setError(errMsg(e, 'Staff ID not found')); beep(false); } finally { setBusy(false); }
  };

  const submit = async (selfie, liveness) => {
    setBusy(true); setError('');
    try {
      const gps = await getGPS(10000);
      const r = await kioskApi.punch({ staff_id: staff.staff_id, gps, selfie, liveness, work_role: role });
      beep(true);
      setResult({ ok: true, title: `${r.kind === 'in' ? 'Check In' : 'Check Out'}${r.session_no > 1 ? ` (session ${r.session_no})` : ''} recorded`, msg: `${r.staff_name}${r.work_role ? ` · ${WORK_ROLES[r.work_role]}` : ''} · ${fmtTime(r.time)} — waiting for supervisor approval${r.on_site === false ? ' (off-site)' : ''}` });
      setStep('done');
    } catch (e) { setError(errMsg(e, 'Could not record attendance — try again')); beep(false); } finally { setBusy(false); }
  };

  // Logged-in supervisor/HR on an un-configured tablet → straight to device setup.
  if (!cfg && localStorage.getItem('session_token')) return <Navigate to="/attendance?tab=devices" replace />;
  if (!cfg) return <div className="min-h-screen bg-slate-900 text-white px-6"><NotSetUp onSetup={() => navigate('/attendance?tab=devices')} /></div>;
  const kind = staff?.next_kind;
  const label = kindLabel(kind, staff?.session_count);

  return (
    <div className="min-h-screen bg-slate-900 text-white select-none" data-testid="kiosk-page">
      <header className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-400">Staff Attendance</div>
          <div className="text-2xl font-bold" data-testid="kiosk-section">{device?.section || cfg.section}</div>
        </div>
        <div className="flex items-center gap-4">
          <button type="button" onClick={() => navigate(`/my-pay?from=kiosk&site=${encodeURIComponent(cfg.site_id || 'salem')}`)}
            className="flex items-center gap-1.5 rounded-full border border-amber-400/60 px-3 py-1.5 text-sm font-semibold text-amber-300 hover:bg-amber-400 hover:text-slate-900 transition-colors" data-testid="kiosk-my-pay-btn">
            <Wallet className="w-4 h-4" />My Pay · என் சம்பளம்
          </button>
          <button type="button" onClick={() => window.open(GUIDES.staff.file, '_blank', 'noopener')} className="flex items-center gap-1 text-sm text-slate-300 hover:text-amber-300" data-testid="kiosk-guide-btn"><BookOpen className="w-4 h-4" />User Guide</button>
          <Clock />
        </div>
      </header>
      <main className="px-6 py-8">
        {error && <div className="max-w-md mx-auto mb-5 rounded-2xl bg-red-500/15 border border-red-400 px-5 py-4 text-lg text-red-100 text-center" data-testid="kiosk-error">{error}</div>}
        {step === 'id' && <KeypadEntry value={sid} onChange={(v) => { setSid(v); setError(''); }} onSubmit={lookup} disabled={busy} />}
        {step === 'confirm' && staff && (
          <div className="max-w-md mx-auto text-center space-y-6 pt-6" data-testid="kiosk-confirm">
            <UserCheck className="w-16 h-16 mx-auto text-amber-400" />
            <div className="text-4xl font-bold" data-testid="kiosk-confirm-name">{staff.staff_name}</div>
            <div className="text-xl text-slate-300" data-testid="kiosk-confirm-section">{staff.section}{staff.work_section ? ` → ${staff.work_section}` : ''} · {staff.staff_id}</div>
            {kind ? (
              <>
                <div className="text-2xl">Is this you?</div>
                {!staff.has_reference && <div className="text-sm text-amber-300" data-testid="kiosk-no-reference">No reference photo yet — your supervisor will check your selfie.</div>}
                {kind === 'in' && staff.worker_type === 'contract' && <WorkRolePicker value={role} onChange={setRole} />}
                <div className="flex gap-4 justify-center">
                  <Button onClick={reset} variant="outline" className="h-16 px-10 text-xl rounded-2xl bg-transparent text-white border-slate-500" data-testid="kiosk-confirm-no">No</Button>
                  <Button onClick={() => setStep('selfie')} className={`h-16 px-10 text-xl rounded-2xl ${kind === 'in' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-indigo-500 hover:bg-indigo-600'}`} data-testid="kiosk-confirm-yes">
                    {kind === 'in' ? <LogIn className="w-6 h-6 mr-2" /> : <LogOut className="w-6 h-6 mr-2" />}Yes — {label}
                  </Button>
                </div>
                {staff.session_count > 0 && <div className="text-sm text-slate-400" data-testid="kiosk-session-count">Session {Math.min(staff.session_count + (kind === 'in' ? 1 : 0), staff.max_sessions)} of up to {staff.max_sessions} today</div>}
                {staff.after_midnight && <div className="text-sm text-indigo-300" data-testid="kiosk-after-midnight">OT check-out for {staff.day} — yesterday's session is still open (until {staff.day_close} AM).</div>}
              </>
            ) : (
              <>
                <div className="text-xl text-emerald-300" data-testid="kiosk-already-done">All {staff.max_sessions} Check In / Check Out sessions are already recorded for today.</div>
                <Button onClick={reset} className="h-14 px-10 text-lg rounded-2xl" data-testid="kiosk-back">OK</Button>
              </>
            )}
          </div>
        )}
        {step === 'selfie' && staff && (
          <div className="max-w-lg mx-auto space-y-4" data-testid="kiosk-selfie-step">
            <div className="text-center text-2xl font-semibold">{staff.staff_name} — look at the camera</div>
            <SelfieStep big busy={busy} liveness={!!staff.liveness_required} onSubmit={submit} onCancel={reset} submitLabel={`Submit ${label}`} testId="kiosk-selfie" />
            <div className="text-center"><button onClick={reset} className="text-slate-400 underline text-lg" data-testid="kiosk-cancel">Cancel</button></div>
          </div>
        )}
        {step === 'done' && result && <Done result={result} />}
        {busy && step === 'id' && <Loader2 className="w-10 h-10 animate-spin mx-auto mt-6 text-amber-400" />}
      </main>
    </div>
  );
}
